import React, { useState, useRef } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import emailjs from '@emailjs/browser';
import AnimatedSection from '../components/AnimatedSection';
import { emailConfig, recaptchaConfig } from '../config/emailConfig';
import './Contact.css';

const Contact = () => {
  const form = useRef();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    message: ''
  });

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [captchaValue, setCaptchaValue] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const recaptchaRef = useRef();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const sendNotificationEmail = async (templateParams) => {
    // Parameters for the Contact Us template
    const params = {
      to_email: 'solvrcontact@gmail.com',
      from_name: templateParams.from_name,
      from_email: templateParams.email,
      message: templateParams.message,
      'g-recaptcha-response': templateParams['g-recaptcha-response']
    };
    console.log('Sending notification email with params:', params);
    return emailjs.send(
      emailConfig.serviceId,
      emailConfig.notificationTemplateId,
      params,
      emailConfig.publicKey
    );
  };

  const sendAutoReplyEmail = async (templateParams) => {
    // Parameters for the Auto-reply template
    const params = {
      from_name: templateParams.from_name,
      from_email: templateParams.email,  // This will be used as the recipient email
      message: templateParams.message,
      'g-recaptcha-response': templateParams['g-recaptcha-response']
    };
    console.log('Sending auto-reply email with params:', params);
    return emailjs.send(
      emailConfig.serviceId,
      emailConfig.autoReplyTemplateId,
      params,
      emailConfig.publicKey
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    
    if (!captchaValue) {
      setErrorMessage('Please complete the reCAPTCHA verification.');
      return;
    }

    setIsLoading(true);

    try {
      console.log('Preparing to send emails...');
      
      // Base parameters
      const baseParams = {
        from_name: `${formData.firstName} ${formData.lastName}`,
        email: formData.email,
        message: formData.message,
        'g-recaptcha-response': captchaValue
      };

      // Send both emails
      const [notificationResponse, autoReplyResponse] = await Promise.all([
        sendNotificationEmail(baseParams),
        sendAutoReplyEmail(baseParams)
      ]);

      console.log('Notification email response:', notificationResponse);
      console.log('Auto-reply email response:', autoReplyResponse);
      
      setIsSubmitted(true);
      
      // Reset form after 5 seconds
      setTimeout(() => {
        setIsSubmitted(false);
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          message: ''
        });
        setCaptchaValue(null);
        if (recaptchaRef.current) {
          recaptchaRef.current.reset();
        }
      }, 5000);

    } catch (error) {
      console.error('Failed to send email:', error);
      console.error('Error details:', {
        message: error.message,
        text: error.text,
        status: error.status
      });
      setErrorMessage(
        `Error sending message: ${error.text || error.message || 'Unknown error'}. Please try again or contact us directly at solvrcontact@gmail.com`
      );
    } finally {
      setIsLoading(false);
    }
  };

  const onCaptchaChange = (value) => {
    setCaptchaValue(value);
  };

  return (
    <div className="contact">
      {/* Hero Section */}
      <section className="contact-hero">
        <div className="container">
          <h1>Contact Us</h1>
          <p>Ready to start your project? Let's discuss how we can help bring your vision to life.</p>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="contact-form-section">
        <div className="container">
          <AnimatedSection animationType="fadeInUp">
            <div className="contact-content">
            <div className="contact-info">
              <h2>Get in Touch</h2>
              <p>
                Interested in working together? Fill out some info and we will be in touch shortly. 
                We can't wait to hear from you!
              </p>
              
              <div className="contact-details">
                <div className="contact-item">
                  <h3>Email</h3>
                  <p>
                    <a href="mailto:solvrcontact@gmail.com">solvrcontact@gmail.com</a>
                  </p>
                </div>
                
                <div className="contact-item">
                  <h3>Phone</h3>
                  <p>
                    <a href="tel:815-999-5245">(815) 999-5245</a>
                  </p>
                </div>
                
                <div className="contact-item">
                  <h3>Locations</h3>
                  <p>Chicago, IL</p>
                  <p>Durham, NC</p>
                  <p>Coralville, IA</p>
                </div>
              </div>
            </div>

            <div className="form-container">
              {isSubmitted ? (
                <div className="success-message">
                  <h3>Thank you for contacting us!</h3>
                  <p>We will get back to you shortly.</p>
                </div>
              ) : (
                <form ref={form} onSubmit={handleSubmit} className="contact-form">
                  {errorMessage && (
                    <div className="error-message">
                      {errorMessage}
                    </div>
                  )}
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="firstName">First Name *</label>
                      <input
                        type="text"
                        id="firstName"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="lastName">Last Name *</label>
                      <input
                        type="text"
                        id="lastName"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="email">Email *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="message">Message *</label>
                    <textarea
                      id="message"
                      name="message"
                      rows="6"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Tell us about your project..."
                      required
                    ></textarea>
                  </div>
                  
                  <div className="form-group">
                    <ReCAPTCHA
                      ref={recaptchaRef}
                      sitekey={recaptchaConfig.siteKey}
                      onChange={onCaptchaChange}
                    />
                  </div>
                  
                  <button 
                    type="submit" 
                    className="submit-button"
                    disabled={isLoading || !captchaValue}
                  >
                    {isLoading ? 'Sending...' : 'Send Message'}
                  </button>
                </form>
              )}
            </div>
            </div>
          </AnimatedSection>
        </div>
      </section>


    </div>
  );
};

export default Contact; 